import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import emailjs from 'emailjs-com';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import '@/components/sections/ContactForm.scss';
import { Send } from 'lucide-react';
import SlideToVerify from '@/components/ui/SlideToVerify';

type FormValues = {
  fullName: string;
  company: string;
  email: string;
  phone: string;
  message: string;
};

const ContactForm: React.FC = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isHuman, setIsHuman] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
    reset,
  } = useForm<FormValues>();

  const phoneValue = watch('phone') as string;

  const onSubmit = async (data: FormValues) => {
    if (!isHuman) return;
    setAnimating(true);
    setIsSubmitting(true);
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID!,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID!,
        data,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY!
      );
      setTimeout(() => {
        reset();
        setFormSubmitted(true);
      }, 800);
    } catch (err) {
      console.error(err);
      setAnimating(false);
      setIsSubmitting(false);
      alert('Failed to send message. Please try again.');
    }
  };

  return (
    <div className="contact-form">
      <AnimatePresence mode="wait">
        {!formSubmitted ? (
          <motion.form
            key="form"
            className="contact-form__body"
            onSubmit={handleSubmit(onSubmit)}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.4 } }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <h2 className="contact-form__title">Contact</h2>
            <div className="contact-form__grid">
              <div className={`contact-form__field ${errors.fullName ? 'contact-form__field--error' : ''}`}>
                <input {...register('fullName', { required: true })} placeholder=" " disabled={isSubmitting} />
                <label>Full Name</label>
              </div>
              <div className={`contact-form__field ${errors.company ? 'contact-form__field--error' : ''}`}>
                <input {...register('company', { required: true })} placeholder=" " disabled={isSubmitting} />
                <label>Company</label>
              </div>
              <div className={`contact-form__field ${errors.email ? 'contact-form__field--error' : ''}`}>
                <input
                  type="email"
                  {...register('email', {
                    required: true,
                    pattern: { value: /^\S+@\S+$/i, message: 'Invalid email' },
                  })}
                  placeholder=" "
                  disabled={isSubmitting}
                />
                <label>Email Address</label>
              </div>
              <div
                className={`contact-form__field contact-form__phone ${errors.phone ? 'contact-form__field--error' : ''} ${phoneValue ? 'contact-form__phone--filled' : ''}`}
              >
                <Controller
                  control={control}
                  name="phone"
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <PhoneInput
                      value={value}
                      onChange={onChange}
                      defaultCountry="US"
                      international
                      countryCallingCodeEditable={true}
                      disabled={isSubmitting}
                    />
                  )}
                />
                <label>Phone</label>
              </div>
              <div className={`contact-form__field contact-form__message ${errors.message ? 'contact-form__field--error' : ''}`}>
                <textarea
                  {...register('message', { required: true, maxLength: 100 })}
                  placeholder="Your message here"
                  rows={4}
                  disabled={isSubmitting}
                />
                <label>Message</label>
              </div>
              <div className="contact-form__verification">
                <SlideToVerify 
                  onVerify={() => setIsHuman(true)} 
                  disabled={isSubmitting || animating || isHuman} 
                />
              </div>
              <div className="contact-form__submit-wrap">
                <motion.button
                  type="submit"
                  disabled={!isHuman || isSubmitting}
                  className={`contact-form__submit ${isSubmitting ? 'contact-form__submit--sending' : ''} ${!isHuman ? 'contact-form__submit--disabled' : ''}`}
                  whileHover={isHuman && !isSubmitting ? { scale: 1.05 } : {}}
                  whileTap={isHuman && !isSubmitting ? { scale: 0.95 } : {}}
                >
                  <motion.span className={`contact-form__submit-icon ${animating ? 'contact-form__submit-icon--animating' : ''}`}>
                    <Send size={24} strokeWidth={2} />
                  </motion.span>
                </motion.button>
              </div>
            </div>
          </motion.form>
        ) : (
          <motion.div
            key="thanks"
            className="contact-form__thanks"
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ type: 'spring', damping: 15, stiffness: 100 }}
          >
            <h2>Thank you for reaching out!</h2>
            <p>We'll be in touch with you soon.</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContactForm;
